import {inject, Injectable, Type} from '@angular/core';
import {AssignModalComponent} from '@modules/activities/core/shared-components/card/assign-modal/assign-modal.component';
import {AuthenticationService, UserDataEntity} from '@modules/authentication';
import {ButtonListDialogComponent, ButtonListDialogData} from 'fuse-core/components/button-list/button-list-dialog/button-list-dialog.component';
import {AssignationModalDataInterface} from 'fuse-core/components/card/assign-modal/assignation-modal-data.interface';
import {CardsConfigurationService} from 'fuse-core/components/card/cards-configuration.service';
import {DataCardInterface} from 'fuse-core/components/card/data-card.interface';
import {AsyncRules, LessonGranuleEntity, NavigateToLessonOptions} from '@modules/activities/core/models';
import {Observable, of, ReplaySubject} from 'rxjs';
import {AuthorizationService} from '@modules/authorization';
import {filter, mergeMap, take, tap} from 'rxjs/operators';
import {Roles} from 'shared/models';
import {LessonsService} from './lessons.service';
import {DataEntity, OctopusConnectService} from 'octopus-connect';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ActivitiesService} from '@modules/activities/core/activities.service';
import {CommunicationCenterService} from '@modules/communication-center';
import {toSignal} from '@angular/core/rxjs-interop';
import {FreemiumPremiumService} from 'shared/fremium-premium/freemium-premium.service';
import {FuseConfirmDialogComponent} from 'fuse-core/components/confirm-dialog/confirm-dialog.component';
import {LessonsConfigurationService} from './lessons-configuration.service';
import {TranslateService} from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class LessonsCardService {

    private authorizationService = inject(AuthorizationService);
    private lessonsService = inject(LessonsService);
    private activitiesService = inject(ActivitiesService);
    private dialog = inject(MatDialog);
    private octopusConnect = inject(OctopusConnectService);
    private communicationCenter = inject(CommunicationCenterService);
    /* todo virer */
    private cardsConfigurationService = inject(CardsConfigurationService);
    private authService = inject(AuthenticationService);
    private freemiumService = inject(FreemiumPremiumService);
    private lessonsConfigurationService = inject(LessonsConfigurationService);
    private translate = inject(TranslateService);

    public askNumberOfViewers = false;

    private isAutoAssignmentForced = toSignal(this.communicationCenter.getRoom('assignation').getSubject('isAutoAssignmentForced'));
    private learners = toSignal(this.communicationCenter.getRoom('groups-management').getSubject('learnerList'));
    private classes = toSignal(this.communicationCenter.getRoom('groups-management').getSubject('groupsList'));
    private workgroups = toSignal(this.communicationCenter.getRoom('groups-management').getSubject('workgroupsList'));

    constructor() {
        this.communicationCenter.getRoom('activities')
            .getSubject('openAssignmentWithUserDataFormModal').subscribe((data: {
            resource: LessonGranuleEntity,
        }) => this.openAssignmentWithUserDataFormModalDataCardComponent(data.resource));

        this.communicationCenter.getRoom('activities')
            .getSubject('askNumberOfViewers')
            .subscribe((data: boolean) => this.askNumberOfViewers = data);
    }

    public getCallbacks(): Partial<DataCardInterface<LessonGranuleEntity>> {
        return {
            'isEditableAndErasable$': (resource) => this.authorizationService.currentUserCan(AsyncRules.EditLesson, of(false), resource),
            'isDuplicable$': (resource) => this.authorizationService.currentUserCan(AsyncRules.DuplicateLesson, of(false), resource),
            'openAssign': (resource) => this.openAssign(resource),
            'openDialog': (resource) => this.lessonsService.openDeleteDialog(resource),
            'openDuplicate': (id) => this.lessonsService.launchEditor(id),
            'openEditor': (id) => this.lessonsService.launchEditor(id),
            'editAndDeleteAssignments': (resource) => this.lessonsService.editAndDeleteAssignments(resource).subscribe(),
            'play': (resource: LessonGranuleEntity, options: NavigateToLessonOptions) => this.playLessonButVeryBadWay(resource, options) // this.lessonsService.navigateToLesson(resource as LessonGranuleEntity, options),
        };

    }

    public async openAssign(lesson: LessonGranuleEntity): Promise<void> {
        // TODO mettre ça ailleurs pour que ce soit géré par les card ou par la page présentation de lesson
        if (this.activitiesService.assignmentViewFn) {
            const component = await this.activitiesService.assignmentViewFn(); // ✅ ici on exécute la fonction
            this.activitiesService.assignmentView = component;

            const dialogRef = this.dialog.open(this.activitiesService.assignmentView, {
                data: {
                    nodeId: lesson.id
                }
            });

            dialogRef.afterClosed().subscribe((data) => {
                if (data && (!lesson.get('locked') || (<any>lesson).get('locked') === '0')) {
                    const entity = new DataEntity('granule-lesson', <any>lesson.attributes, this.octopusConnect, lesson.id);
                    entity.set('locked', true);
                    entity.save()
                        .subscribe(success => {
                            if (success) {
                                if (this.lessonsConfigurationService.settings.showInfoToTeacherAfterAssignment === true && data) {
                                    this.openInfoAssignmentDialog();
                                }
                                try {
                                    this.lessonsService.userLessonsPaginated.paginator.reload();
                                } catch (e) {
                                    console.error(e);
                                }
                            }
                        });
                }
            });
        }
    }

    private openInfoAssignmentDialog(): void {
        let dialogBody = '';

        const dialogConfig = new MatDialogConfig();

        dialogConfig.data = {
            titleDialog: '',
        };

        this.translate
            .get('generic.info_assignment_teacher')
            .subscribe(
                (translation: string) => (dialogBody = translation)
            );

        this.translate
            .get('generic.info_assignment_teacher_title')
            .subscribe(
                (translation: string) => (
                    dialogConfig.data.titleDialog = translation
                )
            );
        dialogConfig.data.bodyDialog = dialogBody;
        this.dialog.open(FuseConfirmDialogComponent, dialogConfig);
    }


    public getLessonTypeClasses(lesson: LessonGranuleEntity): string[] {
        if (Array.isArray(lesson.get('metadatas').lessonType)) {
            return (lesson.get('metadatas').lessonType as { id: string, label: string }[])
                .map((type) => `lesson-type-${type.label.toLocaleLowerCase()}`) || [];
        } else if (lesson.get('metadatas').lessonType) {
            let label = (lesson.get('metadatas').lessonType as { id: string, label: string }).label;
            return [`lesson-type-${label.toLocaleLowerCase()}`];
        }
    }

    public playSession(lesson: LessonGranuleEntity): void {
        /** TODO faut simplifier ici :
         *   - soit on ne vient pas d'une modal précise et on appelle openAssignmentWithUserDataFormModal,
         *   - soit on vient de cette modal et on appelle play
         *
         *   Alors que play, qui devrait être l'unique appelé (c'est pas a LessonPage de savoir comment ouvrir une lesson) lui aussi ca faire ses tests pour appeler la meme chose que openAssignmentWithUserDataFormModal
         *
         *   Il faut refacto pour appeler uniquement play. Play va gérer le fait qu'il faille ouvrir la modale avant ou non. Parce que c'est pas la résponsabilité de se composant. Rien n'empeche de forcer l'ouverture de la modale dans play avec un argument.
         */
        // get type of modal to use old or new one
        this.communicationCenter
            .getRoom('assignment')
            .getSubject('view').pipe(take(1))
            .subscribe(async (loaderFn: () => Promise<Type<any>>) => {
                const component = await loaderFn();

                // Ne pas tester le nom de composant ! C'est tout sauf une bonne pratique. On ne doit pas essayer de deviner de contexte !
                if (component.name === 'AssignmentByStepsComponent'
                    || !this.cardsConfigurationService.isLaunchLessonAskModalActive((this.authService.accessLevel as Roles))) {
                    this.lessonsService.navigateToLesson(lesson);
                } else if (this.shouldSelectSubLesson(lesson)) {
                    this.openSubLessonSelectionModal(lesson).pipe(
                        take(1),
                        filter((subLessonId) => !!subLessonId),
                        mergeMap((subLessonId) => this.loadLessonById(subLessonId).pipe(take(1))),
                        tap((subLesson) => {
                            this.playAutoAssignment(lesson, subLesson.id.toString());
                        })
                    ).subscribe();
                } else {
                    // ici play sans sous lessons ancienne modal
                    this.openAssignmentWithUserDataFormModal(lesson);
                }
            });
    }

    /**
     * open pop up to set the number of player
     */
    public openAssignmentWithUserDataFormModal(lesson: LessonGranuleEntity, learners = this.learners(), classes = this.classes(), workgroups = this.workgroups(), idToGo = ''): void {
        // TODO - get out of card component, should be passed as parameter

        const config = new MatDialogConfig<AssignationModalDataInterface>();

        config.data = {
            lesson,
            resourceInDataCard: lesson,
            askNumberOfViewers: this.askNumberOfViewers,
            askTitle: this.cardsConfigurationService.isLaunchLessonAskTitle(),
            askComment: this.cardsConfigurationService.isLaunchLessonAskComment(),
            commentMaxLength: this.cardsConfigurationService.launchLessonCommentMaxLength(),
            learners,
            classes,
            workgroups,
        };

        const confirmDialogRef = this.dialog.open<AssignModalComponent, AssignationModalDataInterface, {
            comment: string,
            numberOfViewers: number,
            title: string,
            datacard: DataCardInterface<LessonGranuleEntity>,
            groups: string[]
        }>(AssignModalComponent, config);

        confirmDialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.launchLessonAutoAssignement(
                    lesson,
                    !!result.numberOfViewers ? result.numberOfViewers : null,
                    !!result.comment ? result.comment : null,
                    !!result.title ? result.title : null,
                    result.groups || [],
                    {idToGo: idToGo}
                );
            }
        });
    }

    /**
     * launch lesson and assign lesson to current user
     */
    launchLessonAutoAssignement(lesson: LessonGranuleEntity, nbrePeople?: number, comment?: string, title?: string, groups?: string[], options?: {
        [key: string]: any
    }): void {
        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((user: UserDataEntity) => {
                this.createUserAssignment(user, lesson, nbrePeople, comment, title, groups, options);
            });
    }

    private createUserAssignment(user: UserDataEntity, lesson: LessonGranuleEntity, nbrePeople?: number, comment?: string, title?: string, groups?: string[], options?: {
        [key: string]: any
    }): void {
        // TODO - get out of card component, should be passed as parameter
        const assignmentData = {
            learners: [
                {
                    id: user.id,
                }
            ],
            assignatedCount: nbrePeople,
            comment,
            title,
            project: null,
            rating_base: 4,
            startDate: Date.now(),
            startTime: null,
            dueDate: null,
            dueTime: null,
            nodeId: lesson.id,
            group: [],
            groups: groups ? groups : []
        };


        this.communicationCenter
            .getRoom('assignment')
            .next('createAssignment', {
                assignment: assignmentData, callback: assignment => {
                    // lock lesson
                    if ((<any>lesson).get('locked') !== '1') {
                        const entity = new DataEntity('granule-lesson', <any>lesson.attributes, this.octopusConnect, lesson.id);
                        entity.set('locked', true);
                        entity.save();
                    }
                    // launch assignment
                    this.communicationCenter
                        .getRoom('assignment')
                        .next('launchAssignment', assignment);
                    // run lesson
                    let index = 0;

                    if (options?.idToGo && options?.idToGo !== '') {
                        index = lesson.get('reference').findIndex((seance) => +seance.id === +options?.idToGo);
                    } else {
                        index = lesson.get('reference').findIndex((seance) => +seance.id === +lesson.id);
                    }
                    if (options?.startOnStepIndex) {
                        index = options.startOnStepIndex;
                    }
                    // this.isOneLessonIsLaunchState = false;
                    this.lessonsService.navigateToLesson(lesson, <any>{startOnStepIndex: index !== -1 && !!index ? index : 0}); // TODO
                }
            });
    }

    public openSubLessonSelectionModal(lesson: LessonGranuleEntity): Observable<string | number> {
        if (this.freemiumService.isFreemiumPremiumFeatureEnabled()) {
            const subLessons = lesson.get('reference')?.map((subLesson: any) => {
                const isFree = subLesson?.isFree ?? false;
                let isEnabledForFreemium = true;

                if (isFree) {
                    isEnabledForFreemium = true;
                } else if (!isFree && this.freemiumService.isPremiumMode()) {
                    isEnabledForFreemium = true;
                } else if (!this.freemiumService.isPremiumMode() && !isFree) {
                    isEnabledForFreemium = false;
                }
                return {
                    id: subLesson.id,
                    label: subLesson.title,
                    value: String(subLesson.id),
                    isFree,
                    isEnabledForFreemium,
                };
            }) ?? [];

            return this.dialog.open(
                ButtonListDialogComponent,
                {
                    data: {
                        title: lesson.get('metadatas')?.title ?? '',
                        list: subLessons,
                        isFreemiumPremiumFeatureEnabled: this.freemiumService.isFreemiumPremiumFeatureEnabled()
                    }
                }
            ).afterClosed();
        } else {
            return this.dialog.open(ButtonListDialogComponent, {
                data: {
                    title: lesson.get('metadatas').title,
                    list: lesson.get('reference').map((subLesson: { id: string | number, title: string }) => {
                        return ({label: subLesson.title, value: subLesson.id});
                    })
                }
            }).afterClosed();
        }
    }


    public shouldSelectSubLesson(lesson: LessonGranuleEntity): boolean {
        const isNeeded = lesson.get('reference') !== undefined
            && lesson.get('reference').every((ref: any) => ref.type === 'lesson');
        let isAllowed = false;

        const role = this.authService.accessLevel;
        if (role in this.cardsConfigurationService.chooseSubLessonFeatureActive()) {
            isAllowed = this.cardsConfigurationService.chooseSubLessonFeatureActive()[role];
        } else if ('default' in this.cardsConfigurationService.chooseSubLessonFeatureActive()) {
            isAllowed = this.cardsConfigurationService.chooseSubLessonFeatureActive()['default'];
        }

        return isNeeded && isAllowed;
    }


    /**
     * open pop up to set the number of player
     */
    public openAssignmentWithUserDataFormModalDataCardComponent(entity: LessonGranuleEntity, idToGo = ''): void {
        this.openAssignmentWithUserDataFormModal(entity, this.learners(), this.classes(), this.workgroups(), idToGo);
    }

    private playLessonButVeryBadWay(lesson: LessonGranuleEntity, options: NavigateToLessonOptions): void {
        if (this.isAutoAssignmentForced() && !(options?.preview === true)) { // gère aussi si option est null ou undefined
            if (this.shouldSelectSubLesson(lesson)) {
                this.communicationCenter
                    .getRoom('assignment')
                    .getSubject('view').pipe(take(1))
                    .subscribe(async (loaderFn: () => Promise<Type<any>>) => {
                        const comp = await loaderFn();
                        if (comp.name === 'AssignmentByStepsComponent') {
                            this.openAutoAssignmentDialog(lesson, null);
                        } else {
                            this.openSubLessonSelectionModal(lesson).pipe(
                                take(1),
                                filter((subLessonId) => !!subLessonId),
                                mergeMap((subLessonId) => this.loadLessonById(subLessonId).pipe(take(1))),
                                tap((subLesson) => {
                                    this.playAutoAssignment(lesson, subLesson.id.toString(), options?.quizEnabled);
                                })
                            ).subscribe();
                        }
                    });
            } else {
                // pour lancer si pas de sous parcours et nouvelle modale????
                this.communicationCenter
                    .getRoom('assignment')
                    .getSubject('view').pipe(take(1))
                    .subscribe(async (loaderFn: () => Promise<Type<any>>) => {
                        const comp = await loaderFn();
                        if (comp.name === 'AssignmentByStepsComponent') {
                            this.openAutoAssignmentDialog(lesson, null);
                        } else {
                            this.playAutoAssignment(lesson, null, options?.quizEnabled);
                        }
                    });
            }
        } else {
            this.lessonsService.navigateToLesson(lesson, options);
        }
    }

    private playAutoAssignment(subLesson: LessonGranuleEntity, idToGo: string = '', quizEnabled = false): void {
        if (this.cardsConfigurationService.isLaunchLessonAskModalActive(this.authService.accessLevel as Roles)) {
            this.openAssignmentWithUserDataFormModalDataCardComponent(subLesson, idToGo);
        } else {
            this.launchLessonAutoAssignement(subLesson, null, null, null, null, quizEnabled ? {startOnStepIndex: 1} : null);
        }
    }

    private openAutoAssignmentDialog(lesson, activities = null): void {
        this.communicationCenter
            .getRoom('assignment')
            .getSubject('view').pipe(take(1))
            .subscribe(async (loaderFn: () => Promise<Type<any>>) => {
                const comp = await loaderFn();
                const dialogRef = this.dialog.open(comp, {
                    data: {
                        origine: 'launchInGroup',
                        nodeId: lesson.id,
                        node: lesson,
                        activities: activities,
                        // sublesson when exist (seances)
                        seances: lesson.get('reference')
                            .filter(elem => elem.type === 'lesson').length > 0 ? lesson.get('reference')
                            .filter(elem => elem.type === 'lesson') // que des lessons for sceance activity are not sceance
                            .map((subLesson: { id: string | number, title: string }) => {
                                return ({label: subLesson.title, value: subLesson.id});
                            }) : []
                    }
                });

                dialogRef.afterClosed().subscribe((data) => {
                    if (data) {
                        // launch old code after new modal by step is close
                        this.loadLessonById(data.sceances.filter(s => s.selected === true).length > 0 ? data.sceances.filter(s => s.selected === true)[0].value : data.lesson).pipe(take(1)).subscribe(dataEntity => {
                            this.launchLessonAutoAssignementDataCardComponent(dataEntity, data?.numberOfLearners, '', lesson.get('metadatas')?.title);//, data?.groups);
                        });
                    }
                });
            });
    }

    /**
     * launch lesson and assign lesson to current user
     */
    launchLessonAutoAssignementDataCardComponent(dataEntity: DataEntity, nbrePeople?: number, comment?: string, title?: string): void {
        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((user: UserDataEntity) => {
                this.createUserAssignmentDataCardComponent(user, dataEntity, nbrePeople, comment, title);
            });
    }

    /**
     * prepare data and launch create user assignment in assignation
     * @param user to assign
     * @param dataCard datacard info
     * @param nbrePeople number of people who will see the lesson
     * @param comment
     * @param title if not set, the backend will generate a title
     */
    private createUserAssignmentDataCardComponent<T extends DataEntity>(user: UserDataEntity, dataEntity: T, nbrePeople?: number, comment?: string, title?: string): void {
        const assignmentData = {
            learners: [
                {
                    id: user.id,
                }
            ],
            assignatedCount: nbrePeople,
            comment,
            title,
            project: null,
            rating_base: 4,
            startDate: Date.now(),
            startTime: null,
            dueDate: null,
            dueTime: null,
            nodeId: dataEntity.id,
            group: []
        };

        this.communicationCenter
            .getRoom('assignment')
            .next('createAssignment', {
                assignment: assignmentData, callback: assignment => {
                    // lock lesson
                    if (dataEntity.get('locked') !== '1') {
                        const entity = new DataEntity('granule-lesson', dataEntity.attributes, this.octopusConnect, dataEntity.id);
                        entity.set('locked', true);
                        entity.save();
                    }
                    // launch assignment
                    this.communicationCenter
                        .getRoom('assignment')
                        .next('launchAssignment', assignment);
                    // run lesson
                    this.lessonsService.navigateToLesson(dataEntity as LessonGranuleEntity);
                }
            });
    }

    public loadLessonById(subLessonId: string | number): Observable<DataEntity> {
        const lesson$ = new ReplaySubject<Observable<DataEntity>>(1);

        this.communicationCenter
            .getRoom('lessons')
            .next('getLesson', {
                lessonId: subLessonId,
                callbackSubject: lesson$
            });

        return lesson$.pipe(
            mergeMap(obs => obs)
        );
    }

}